import { Link, MainLayout, Section } from "@arp/react-ndbx";
import { Paper, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import React from "react";

import Page from "../components/Page";

import { URLS } from "../config/const/enums";
import { pageName } from "../config/pageInfo/pageNames.js";
import { useGetReferenceData } from "../hooks/dataHooks/useGetReferenceData.js";
import useSharedData from "../hooks/useSharedData";

export default function LifeInfoAndDuty() {
	const theme = useTheme();
	useGetReferenceData(); // Trigger the call to get reference data for pds url
	const { pdsUrl } = useSharedData();

	return (
		<Page
			title="Information & Duty"
			identifier={pageName.INFO_AND_DUTY}
			mt={{ xs: 0 }}
		>
			<Section
				my={{ xs: 0 }}
				py={{ xs: 18 }}
				sx={{ backgroundColor: theme.palette.soft.softBlue500 }}
			>
				<Typography variant="h1">Life Insurance</Typography>
			</Section>
			<MainLayout maxWidth="md">
				<Paper
					elevation={0}
					sm={12}
					sx={{
						maxWidth: { sm: 740 },
						mb: { xs: 10 },
					}}
				>
					<Section xs={12} mt={{ xs: 7, sm: 9 }} align="left" id="duty">
						<Typography variant="h2">
							Duty to take reasonable care not to make a misrepresentation
						</Typography>
					</Section>
					<Section xs={12} mt={{ xs: 5, sm: 7 }} align="left">
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							You must take reasonable care not to make a misrepresentation to
							us. This responsibility applies until we issue you with a policy
							for the first time, or agree to certain variations/changes, or
							agree to reinstate your policy.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							You must answer our questions honestly, accurately and to the best
							of your knowledge. The answers that you give us are vital to our
							decision whether to insure you and on what terms. A
							misrepresentation includes a statement that's false, partially
							false, or which doesn't fairly reflect the truth. It's not
							misrepresentation if you don't answer a question or if your answer
							is obviously incomplete or irrelevant to the question asked.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							The responsibility to take reasonable care not to make a
							misrepresentation applies to everyone who will be insured under
							the policy. If you're answering questions on behalf of anyone,
							we'll treat your answers or representations as theirs.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							Whether or not you've taken reasonable care not to make a
							misrepresentation is to be determined having regard to all
							relevant circumstances, including the type of insurance, who it's
							intended to be sold to, your particular characteristics and
							circumstances we are aware of.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							If you don't meet your duty
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							If you don't meet the above duty, we may reject or not fully pay
							your claim.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							Within the first three years, we may treat your cover as if it
							never existed if we wouldn't have insured you on any terms if you
							had met the duty.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							Instead of treating your cover as if it never existed, we may
							reduce the Cover Amount using a formula prescribed by law (we can
							only do this within the first three years for Life cover). For
							Critical Illness cover and Permanently Unable to Work cover, we
							may also change the terms of your cover to put us in the same
							position we would have been in if you had met the duty.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							If you deliberately provide dishonest or inaccurate answers or
							were reckless in answering our questions, this is an act of fraud,
							and we may treat your cover as if it never existed.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We'll apply these rights separately to Life cover, Critical
							Illness cover and Permanently Unable to Work cover.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Contact Us
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							If you find our information or questions are unclear, you can
							contact us on{" "}
							<Link variant="p3-link-bold" href={URLS.CONTACT_US_PHONE_URL}>
								{URLS.CONTACT_US_PHONE_NUMBER}
							</Link>{" "}
							or visit{" "}
							<Link
								variant="p3-link-bold"
								target="_blank"
								href="https://www.allianz.com.au/misrepresentation-life"
							>
								www.allianz.com.au/misrepresentation-life
							</Link>
						</Typography>
					</Section>

					<Section xs={12} mt={{ xs: 10, sm: 16 }} align="left" id="exclusions">
						<Typography variant="h2">
							Policy Exclusions, exclusion periods and waiting periods
						</Typography>
					</Section>
					<Section xs={12} mt={{ xs: 5, sm: 7 }} align="left">
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							<Typography variant="p3-bold">Note:</Typography> see the{" "}
							<Link variant="p3-link-bold" target="_blank" href={pdsUrl}>
								Product Disclosure Statement
							</Link>{" "}
							for full details. If you have any questions, contact us.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							When is a benefit not payable (policy exclusions)?
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							In the following situations we won't pay a benefit:
						</Typography>
						<Typography
							variant="p3"
							component="ul"
							mb={{ xs: 2 }}
							pl={{ xs: 2 }}
						>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								We won’t pay a Life cover benefit if you commit suicide within
								the first 13 months of the commencement of the cover, any
								increase in cover that you request (but only in respect of the
								increase), or where we've agreed to reinstate the policy after
								it has lapsed.
							</Typography>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								We won’t pay Critical Illness or Permanently Unable to Work
								benefit where the condition is intentionally self-inflicted.
							</Typography>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								We won’t pay any benefit where we've agreed with you a special
								term in respect of your cover that specifically excludes the
								event or condition leading to the claim. Any such special term
								will be agreed with you before your policy is issued and will
								appear on your Policy Schedule.{" "}
								<Typography variant="p3" component="i">
									For example, if we agree to exclude Sickness or Injury as a
									result of 'Mountain climbing' in respect of Permanently Unable
									to Work cover and you were disabled in the course of mountain
									climbing, we wouldn't pay a Permanently Unable to Work
									benefit.
								</Typography>
							</Typography>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								We won’t pay any benefits to the extent a claim arises because
								you didn't follow advice issued by the Australian Government
								relating to an overseas location. This includes travelling when
								a 'Reconsider your need to travel' or 'Do not travel' alert is
								in place and not taking action to minimise or avoid any
								potential claim.
							</Typography>
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Critical Illness cover exclusion period:
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							In the case of Cancer of specified severity, Cardiac Surgery and
							Vascular Disease, we won't pay a benefit if the insured event
							occurs, is first diagnosed, the symptoms leading to the diagnosis
							become apparent, or the recommendation for surgery is made within
							90 days of:
						</Typography>
						<Typography
							variant="p3"
							component="ul"
							mb={{ xs: 2 }}
							pl={{ xs: 2 }}
						>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								the commencement of the benefit;
							</Typography>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								any increase in the benefit (but only in respect of the
								increase); or
							</Typography>
							<Typography variant="p3" component="li" mb={{ xs: 2 }}>
								the reinstatement date where we have agreed to reinstate the
								benefit after it has lapsed.
							</Typography>
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							If one of these conditions occurs again after the 90 day exclusion
							period and isn't related to the first occurrence, a benefit would
							then be paid. This 90 day exclusion period doesn't apply where
							immediately prior to the commencement of cover another insurer
							covered you for the same condition and we agreed to replace the
							cover held with that insurer (and you weren't within the insurer's
							90 day exclusion period).
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Permanently Unable to Work cover waiting period:
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							You must first suffer the Sickness or Injury after the start of
							the policy and while the benefit is current.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							Solely because of Sickness or Injury you've been continuously
							unable to work for at least 90 consecutive days.
						</Typography>
					</Section>

					<Section xs={12} mt={{ xs: 10, sm: 16 }} align="left" id="privacy">
						<Typography variant="h2">Privacy Notice</Typography>
					</Section>
					<Section xs={12} mt={{ xs: 5, sm: 7 }} align="left">
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							At Allianz Life, we give priority to protecting the privacy of
							your personal information. We do this by handling personal
							information in a responsible manner and in accordance with the
							Privacy Act 1988 (Cth).
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							How we collect your personal information
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We usually collect your personal information directly from you or
							your agents. We may also collect it from our agents and service
							providers; other insurers and insurance reference bureaus; people
							who are involved in a claim (including third parties claiming
							under your policy, your employer, external claims data collectors
							and verifiers and medical service providers); third parties who
							may be arranging insurance cover for a group that you're part of;
							law enforcement, dispute resolution, statutory and regulatory
							bodies; marketing lists and industry databases; and publicly
							available sources.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Why we collect your personal information
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We collect your personal information (other than sensitive
							information) to enable us to provide our products and services
							(including to process and settle claims); make offers of products
							and services provided by us, our related companies, brokers,
							intermediaries, business partners and others that we have an
							association with that may interest you; and conduct market or
							customer research to determine those products or services that may
							suit you.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							You can choose not to receive product or service offerings from us
							(including product or service offerings from us on behalf of our
							brokers, intermediaries and/or our business partners) or our
							related companies by calling the Allianz Direct Marketing Privacy
							Service Line on 1300 360 529 (AEDT/AEST) 8am to 6pm Monday to
							Friday, or going to our website's{" "}
							<Link
								variant="p3-link-bold"
								target="_blank"
								href="https://www.allianz.com.au/privacy"
							>
								privacy section
							</Link>
							.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We collect your sensitive information (which may include
							information related to genetic testing) from you for the purpose
							of providing our product and services, including to underwrite
							insurance cover; assess and settle claims; and undertake research
							analysis and design new insurance products.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							If you don't provide your personal (including sensitive)
							information we require, we may not be able to provide you with our
							services, including settlement of claims.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Who we disclose your personal information to
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We may disclose your personal information to others with whom we
							have business arrangements for the purposes listed in the relevant
							paragraph above or (except in the case of sensitive information)
							to enable them to offer their products and services to you. These
							parties may include insurers, intermediaries, reinsurers,
							insurance reference bureaus, related companies, our advisers,
							persons involved in claims, external claims data collectors and
							verifiers, parties that we have an insurance scheme in place with
							under which you purchased your policy (such as a financier).
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We won't disclose your sensitive information for any purpose other
							than the purpose for which it was collected or a directly related
							secondary purpose, unless you otherwise consent.
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We may also disclose your personal (including sensitive)
							information if it's required to be disclosed to government, law
							enforcement, dispute resolution, statutory or regulatory bodies,
							or as required by law.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Disclosure overseas
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							Your personal information may be disclosed to other companies in
							the Allianz Group, business partners, reinsurers and service
							providers that may be located in Australia or overseas. The
							countries this information may be disclosed to will vary from time
							to time, but may include Canada, Germany, New Zealand, United
							Kingdom, United States of America and other countries where the
							Allianz Group has a presence or engages subcontractors. We
							regularly review the security of our systems used for sending
							personal information overseas. Any information disclosed may only
							be used for the purposes of collection detailed above and system
							administration.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Access to your personal information and complaints
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							You may ask for access to the personal information we hold about
							you and seek correction by calling 1300 360 529 (AEDT/AEST) 8am to
							6pm Monday to Friday. Our{" "}
							<Link
								variant="p3-link-bold"
								target="_blank"
								href="https://www.allianz.com.au/privacy"
							>
								privacy policy
							</Link>{" "}
							contains details about how you may make a complaint about a breach
							of the privacy principles contained in the Privacy Act 1988 (Cth)
							and how we deal with complaints.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Telephone call recording
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							We may record incoming and/or outgoing telephone calls for
							training or verification purposes. Where we've recorded a
							telephone call, we can provide you with a copy at your request
							where it's reasonable to do so.
						</Typography>
						<Typography variant="p3-bold" component="p" mb={{ xs: 2 }}>
							Your Consent
						</Typography>
						<Typography variant="p3" component="p" mb={{ xs: 2 }}>
							By providing us with personal (including sensitive) information,
							you and any other person you provide personal information for
							consent to these uses and disclosures until you tell us otherwise.
							If you wish to withdraw your consent, including for things such as
							receiving information on products and offers by us or persons we
							have an association with, contact us.
						</Typography>
					</Section>
				</Paper>
			</MainLayout>
		</Page>
	);
}
